import React, { useState, useEffect } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { fetchContacts, Contact } from '../services/profileService.ts'; // Import contacts service
import { useAccount } from 'wagmi'; // To get current user address

interface StreamConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (config: any) => Promise<void>; // Keep 'any' for now, refine later
    isLoading: boolean;
}

type StreamType = 'public' | 'solo';

const StreamConfigModal: React.FC<StreamConfigModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isLoading,
}) => {
    const { address: connectedAddress } = useAccount();
    const [streamType, setStreamType] = useState<StreamType>('public');
    const [entryToken, setEntryToken] = useState<string>('FREE'); // Default to FREE
    const [entryPrice, setEntryPrice] = useState<string>('');
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [selectedContact, setSelectedContact] = useState<string>(''); // Store target wallet address
    const [scheduleInfo, setScheduleInfo] = useState<string>(''); // Simple text for now
    const [contactsLoading, setContactsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch contacts when modal opens and user is connected
    useEffect(() => {
        if (isOpen && connectedAddress) {
            setContactsLoading(true);
            fetchContacts(connectedAddress)
                .then(setContacts)
                .catch(err => {
                    console.error("Failed to fetch contacts for modal:", err);
                    setError("Could not load contacts.");
                })
                .finally(() => setContactsLoading(false));
        } else {
            // Reset state when closed or not connected
            setContacts([]);
            setSelectedContact('');
            setStreamType('public');
            setEntryToken('FREE');
            setEntryPrice('');
            setScheduleInfo('');
            setError(null);
        }
    }, [isOpen, connectedAddress]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null); // Clear previous errors

        let config: any = {
            streamType,
        };

        if (streamType === 'public') {
            // Public stream specific config (if any) - currently none needed beyond type
        } else if (streamType === 'solo') {
            // Solo stream specific validation and config
            if (!selectedContact) {
                setError("Please select a contact for the solo stream.");
                return;
            }
            const price = parseFloat(entryPrice);
            if (isNaN(price) || price <= 0) {
                setError("Please enter a valid positive entry price in BNB.");
                return;
            }
            if (!scheduleInfo.trim()) {
                setError("Please provide schedule information (e.g., 'Tomorrow at 8 PM EST').");
                return;
            }
            config = {
                ...config,
                targetUserAddress: selectedContact,
                entryPriceBNB: price.toString(), // Send as string
                scheduleInfo: scheduleInfo.trim(),
            };
        }

        console.log("Submitting stream config:", config);
        try {
            await onSubmit(config);
            // onClose(); // Keep modal open during loading, onSubmit should handle close on success
        } catch (submitError: any) {
            console.error("Error during stream config submission:", submitError);
            setError(submitError.message || "Failed to create stream request.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-[#2c3e50] to-[#34495e] p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-500 relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-300 hover:text-white text-2xl font-bold"
                    disabled={isLoading}
                >
                    &times;
                </button>

                <h2 className="text-2xl font-bold mb-6 text-white text-center">Create New Stream</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Stream Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Stream Type</label>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setStreamType('public')}
                                className={`flex-1 py-2 px-4 rounded-md transition-colors ${streamType === 'public' ? 'bg-[#7db4e5] text-white' : 'bg-gray-600 hover:bg-gray-500 text-gray-200'}`}
                                disabled={isLoading}
                            >
                                Public
                            </button>
                            <button
                                type="button"
                                onClick={() => setStreamType('solo')}
                                className={`flex-1 py-2 px-4 rounded-md transition-colors ${streamType === 'solo' ? 'bg-[#7db4e5] text-white' : 'bg-gray-600 hover:bg-gray-500 text-gray-200'}`}
                                disabled={isLoading}
                            >
                                Solo (Private Invite)
                            </button>
                        </div>
                    </div>

                    {/* --- SOLO STREAM OPTIONS --- */}
                    {streamType === 'solo' && (
                        <>
                            {/* Contact Selection */}
                            <div>
                                <label htmlFor="contactSelect" className="block text-sm font-medium text-gray-300 mb-1">Invite Contact</label>
                                {contactsLoading ? (
                                    <div className="flex items-center text-gray-400">
                                        <ClipLoader color={'#ffffff'} size={16} className="mr-2" /> Loading contacts...
                                    </div>
                                ) : contacts.length > 0 ? (
                                    <select
                                        id="contactSelect"
                                        value={selectedContact}
                                        onChange={(e) => setSelectedContact(e.target.value)}
                                        className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white focus:ring-2 focus:ring-[#7db4e5] focus:outline-none"
                                        disabled={isLoading}
                                        required
                                    >
                                        <option value="" disabled>-- Select a contact --</option>
                                        {contacts.map(contact => (
                                            <option key={contact.walletAddress} value={contact.walletAddress}>
                                                {contact.username} ({contact.walletAddress.substring(0, 6)}...)
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="text-sm text-gray-400">You have no contacts to invite. Add contacts from their profile.</p>
                                )}
                            </div>

                            {/* Entry Price */}
                            <div>
                                <label htmlFor="entryPrice" className="block text-sm font-medium text-gray-300 mb-1">Entry Price (BNB - Testnet)</label>
                                <input
                                    type="number"
                                    id="entryPrice"
                                    value={entryPrice}
                                    onChange={(e) => setEntryPrice(e.target.value)}
                                    className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white focus:ring-2 focus:ring-[#7db4e5] focus:outline-none"
                                    placeholder="e.g., 0.01"
                                    min="0.000001" // Example minimum
                                    step="any"
                                    disabled={isLoading}
                                    required
                                />
                            </div>

                            {/* Schedule Info */}
                            <div>
                                <label htmlFor="scheduleInfo" className="block text-sm font-medium text-gray-300 mb-1">Schedule / Time</label>
                                <input
                                    type="text"
                                    id="scheduleInfo"
                                    value={scheduleInfo}
                                    onChange={(e) => setScheduleInfo(e.target.value)}
                                    className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white focus:ring-2 focus:ring-[#7db4e5] focus:outline-none"
                                    placeholder="e.g., Tomorrow 8 PM EST, In 2 hours"
                                    disabled={isLoading}
                                    required
                                />
                            </div>
                        </>
                    )}
                    {/* --- END SOLO STREAM OPTIONS --- */}

                    {/* --- PUBLIC STREAM OPTIONS (Placeholder) --- */}
                    {/* Add public-specific options here if needed in the future */}
                    {/* Example:
                    {streamType === 'public' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Public Stream Title (Optional)</label>
                            <input type="text" ... />
                        </div>
                    )}
                    */}
                    {/* --- END PUBLIC STREAM OPTIONS --- */}


                    {/* Error Message */}
                    {error && (
                        <p className="text-red-400 text-sm text-center">{error}</p>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-center pt-2">
                        <button
                            type="submit"
                            className="bg-[#7db4e5] hover:bg-[#6ca3d1] text-white font-bold py-2 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
                            disabled={isLoading || (streamType === 'solo' && !selectedContact)}
                        >
                            {isLoading ? (
                                <ClipLoader color={'#ffffff'} loading={true} size={20} />
                            ) : streamType === 'solo' ? (
                                'Send Invite'
                            ) : (
                                'Go Live' // Or 'Configure Public Stream'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StreamConfigModal;
